import { Editor, Transforms, Element, Path, Range } from 'slate'
import { ElementWithType } from '../../../../../../utils/types'
import { INDENT_EXCLUDED_TYPES, MAX_INDENT_LEVEL } from '../../../../../../utils/common-rte-utils/constants'
import { getRootListPath } from '../list/utils'

const shouldExcludeFromIndent = (nodeType: string, node: ElementWithType): boolean => {
  return INDENT_EXCLUDED_TYPES.includes(nodeType)
}

// Common helper to get indentable node above selection
const getIndentableNode = (editor): ElementWithType | undefined => {
  const { selection } = editor
  if (selection === null) return undefined

  const entry = Editor.above(editor, {
    at: selection,
    match: (n: ElementWithType) => Element.isElement(n) && !shouldExcludeFromIndent(n.type, n),
    mode: 'lowest'
  })
  const node = entry?.[0] as ElementWithType | undefined
  return node
}

const formIndentAttributes = (attrs, indentLevel) => {
  const elementAttribute = {
    ...attrs
  }

  if (indentLevel > 0) {
    elementAttribute['data-indent-level'] = indentLevel
  } else {
    delete elementAttribute['data-indent-level']
  }

  if (attrs?.style) {
    elementAttribute.style = { ...attrs?.style }
    delete elementAttribute.style.marginLeft
    delete elementAttribute.style.paddingLeft

    if (indentLevel === 0 && Object.keys(elementAttribute.style).length === 0) {
      delete elementAttribute.style
    }
  }

  return { attrs: elementAttribute }
}

const canModifyIndent = (node: ElementWithType): boolean => {
  return !node.attrs?.style?.["text-align"]
}

export const indentNode = (editor) => {
  const { selection } = editor
  if (selection === null) {
    return
  }

  for (const [n, p] of Editor.nodes(editor, { at: selection })) {
    const node: ElementWithType = n as ElementWithType
    const path = p
    
    if (Element.isElement(node) && !shouldExcludeFromIndent(node.type, node) && canModifyIndent(node)) {
      const currentIndent = getCurrentIndentLevel(node)
      if (currentIndent >= MAX_INDENT_LEVEL) {
        return 
      }
      
      const newIndentLevel = currentIndent + 1
      const elementAttribute = formIndentAttributes(node.attrs || {}, newIndentLevel)
      
      Transforms.setNodes(editor, elementAttribute as ElementWithType, { at: path })
    }
  }
}

export const outdentNode = (editor) => {
  const { selection } = editor
  if (selection === null) {
    return
  }

  for (const [n, p] of Editor.nodes(editor, { at: selection })) {
    const node: ElementWithType = n as ElementWithType
    const path = p
    
    if (Element.isElement(node) && !shouldExcludeFromIndent(node.type, node) && canModifyIndent(node)) {
      const currentIndent = getCurrentIndentLevel(node)
      const newIndentLevel = Math.max(currentIndent - 1, 0) // Min 0 levels
      const elementAttribute = formIndentAttributes(node.attrs || {}, newIndentLevel)
      
      Transforms.setNodes(editor, elementAttribute as ElementWithType, { at: path })
    }
  }
}

export const getCurrentIndentLevel = (node: ElementWithType) => {
  const indentLevel = node.attrs?.['data-indent-level']
  return indentLevel || 0
}

export const isNodeIndented = (editor) => {
  const node = getIndentableNode(editor)
  if (!node || !canModifyIndent(node)) return false
  return getCurrentIndentLevel(node) > 0
}

export const isNodeOutdented = isNodeIndented

export const isNodeAtMaxIndent = (editor) => {
  const node = getIndentableNode(editor)
  if (!node || !canModifyIndent(node)) return false
  return getCurrentIndentLevel(node) >= MAX_INDENT_LEVEL
}


const isListItemAtRoot = (editor, liPath: Path): boolean => {
  const rootListPath = getRootListPath(editor, liPath)
  
  if (!rootListPath) return true // No root list found, treat as root level
  return Path.isChild(liPath, rootListPath)
}

export const isNodeNotIndented = (editor) => {
  const { selection } = editor
  if (!selection) return true

  // Check if we're in a list item first
  const [liEntry] = Editor.nodes(editor, {
    match: (n: ElementWithType) => n.type === 'li',
    mode: 'lowest'
  })

  if (liEntry) {
    const liPath = liEntry[1]
    return isListItemAtRoot(editor, liPath)
  }

  let hasIndentedNode = false
  for (const [n] of Editor.nodes(editor, { at: selection })) {
    const node = n as ElementWithType
    if (Element.isElement(node) && !shouldExcludeFromIndent(node.type, node) && canModifyIndent(node)) {
      if (getCurrentIndentLevel(node) > 0) {
        hasIndentedNode = true
        break
      }
    }
  }
  return !hasIndentedNode
}

export const isNodeAligned = (editor) => {
  const { selection } = editor
  if (selection === null) {
    return false
  }

  const [match] = Editor.nodes(editor, {
    at: selection,
    match: (n: ElementWithType) => {
      if (Element.isElement(n) && !shouldExcludeFromIndent(n.type, n)) {
        return !!n.attrs?.style?.["text-align"]
      }
      return false
    }
  })

  return !!match
}

export const isInTable = (editor) => {
  const { selection } = editor
  if (!selection) return false
  
  const [cell] = Editor.nodes(editor, {
    match: (n: ElementWithType) => n.type === 'td' || n.type === 'th',
    mode: 'lowest',
  })
  return !!cell
}

